import React, { useMemo, useState } from 'react';
import { Flame, ChevronDown, ChevronUp, Search, ExternalLink } from 'lucide-react';

const BAND_STYLES = {
    hot: 'bg-red-100 text-red-700',
    warm: 'bg-amber-100 text-amber-700',
    watching: 'bg-[#E0F7FA] text-[#3a99a3]',
    low: 'bg-gray-100 text-gray-500',
};

function relativeTime(value) {
    if (!value) return 'n/a';
    const diffMs = Date.now() - new Date(value).getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(value).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
}

/**
 * "Top buyers to contact today", ranked by Intent Score, with the one thing the
 * agent should do next. Tapping a row opens the buyer quick-actions popup.
 */
export default function TopBuyersTable({ buyers = [], onBuyerClick, initialCount = 10 }) {
    const [expanded, setExpanded] = useState(false);
    const [query, setQuery] = useState('');
    const [openScore, setOpenScore] = useState(null);

    const filtered = useMemo(() => {
        const needle = query.trim().toLowerCase();
        if (!needle) return buyers;
        return buyers.filter((b) =>
            (b.name || '').toLowerCase().includes(needle)
            || (b.email || '').toLowerCase().includes(needle));
    }, [buyers, query]);

    const visible = expanded ? filtered : filtered.slice(0, initialCount);

    if (!buyers.length) {
        return (
            <p className="rounded-xl border border-dashed border-gray-200 p-6 text-center text-sm text-gray-500">
                No buyer activity recorded for this property yet.
            </p>
        );
    }

    return (
        <div>
            <div className="mb-3 flex items-center gap-2">
                <div className="relative flex-1 max-w-xs">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Find a buyer"
                        className="w-full rounded-lg border border-gray-200 py-1.5 pl-8 pr-3 text-sm focus:border-[#55C5D0] focus:outline-none"
                    />
                </div>
                <span className="text-xs font-medium text-gray-500">
                    {filtered.length} buyer{filtered.length === 1 ? '' : 's'}
                </span>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full min-w-[860px] text-sm">
                    <thead>
                        <tr className="border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-500">
                            <th className="py-2 pr-2 font-semibold">Buyer</th>
                            <th className="px-2 py-2 font-semibold">Intent</th>
                            <th className="px-2 py-2 font-semibold">Latest activity</th>
                            <th className="px-2 py-2 text-right font-semibold">Views</th>
                            <th className="px-2 py-2 font-semibold">Last visit</th>
                            <th className="px-2 py-2 font-semibold">Enquiry</th>
                            <th className="px-2 py-2 font-semibold">Offer</th>
                            <th className="px-2 py-2 font-semibold">Agent</th>
                            <th className="px-2 py-2 font-semibold">Next action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {visible.map((buyer) => (
                            <React.Fragment key={buyer.key}>
                                <tr
                                    className="border-b border-gray-100 align-top hover:bg-[#f8fafb]"
                                >
                                    <td className="py-2.5 pr-2">
                                        <button
                                            type="button"
                                            onClick={() => onBuyerClick && onBuyerClick(buyer)}
                                            className="flex items-center gap-2 text-left"
                                        >
                                            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#E0F7FA] text-xs font-bold text-[#3a99a3]">
                                                {(buyer.name || '?').charAt(0).toUpperCase()}
                                            </span>
                                            <span className="min-w-0">
                                                <span className="flex items-center gap-1 font-semibold text-[#374151]">
                                                    {buyer.name}
                                                    {!buyer.is_guest && <ExternalLink size={11} className="text-gray-400" />}
                                                </span>
                                                {buyer.email && (
                                                    <span className="block truncate text-xs text-gray-500">{buyer.email}</span>
                                                )}
                                            </span>
                                        </button>
                                    </td>
                                    <td className="px-2 py-2.5">
                                        <button
                                            type="button"
                                            onClick={() => setOpenScore(openScore === buyer.key ? null : buyer.key)}
                                            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold ${BAND_STYLES[buyer.intent_band] || BAND_STYLES.low}`}
                                        >
                                            {buyer.intent_band === 'hot' && <Flame size={11} />}
                                            {buyer.intent_score}
                                            <span className="font-medium">{buyer.intent_band_label}</span>
                                            {openScore === buyer.key ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                                        </button>
                                    </td>
                                    <td className="px-2 py-2.5 text-xs text-gray-600">{buyer.latest_activity || 'n/a'}</td>
                                    <td className="px-2 py-2.5 text-right font-semibold text-[#374151]">{buyer.views}</td>
                                    <td className="px-2 py-2.5 text-xs text-gray-600">{relativeTime(buyer.last_seen)}</td>
                                    <td className="px-2 py-2.5 text-xs text-gray-600">
                                        {buyer.enquiries ? (buyer.enquiry_status || 'Enquired') : 'n/a'}
                                    </td>
                                    <td className="px-2 py-2.5 text-xs text-gray-600">
                                        {buyer.offers ? (buyer.offer_status || 'Offer made') : 'n/a'}
                                    </td>
                                    <td className="px-2 py-2.5 text-xs text-gray-600">{buyer.agent_name || 'Unassigned'}</td>
                                    <td className="px-2 py-2.5 text-xs font-semibold text-[#3a99a3]">{buyer.next_action}</td>
                                </tr>
                                {openScore === buyer.key && (
                                    <tr className="border-b border-gray-100 bg-[#f8fafb]">
                                        <td colSpan={9} className="px-2 py-3">
                                            <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-gray-500">
                                                Why this score
                                            </p>
                                            <div className="flex flex-wrap gap-2">
                                                {(buyer.intent_signals || []).map((signal, index) => (
                                                    <span
                                                        key={index}
                                                        className="rounded-full bg-white px-2.5 py-1 text-xs text-[#374151] shadow-sm"
                                                    >
                                                        {signal.label}
                                                        {signal.count ? ` (${signal.count})` : ''}
                                                        <span className="ml-1 font-bold text-[#3a99a3]">+{signal.points}</span>
                                                    </span>
                                                ))}
                                                {!(buyer.intent_signals || []).length && (
                                                    <span className="text-xs text-gray-500">No scoring signals recorded.</span>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>

            {filtered.length > initialCount && (
                <button
                    type="button"
                    onClick={() => setExpanded((prev) => !prev)}
                    className="mt-3 flex w-full items-center justify-center gap-1 rounded-lg border border-gray-200 py-2 text-sm font-semibold text-[#3a99a3] hover:bg-[#f0fafb]"
                >
                    {expanded
                        ? (<><ChevronUp size={14} /> Show top {initialCount}</>)
                        : (<><ChevronDown size={14} /> Show all {filtered.length} buyers</>)}
                </button>
            )}
        </div>
    );
}
